import { Component, ViewChild, TemplateRef, AfterViewInit, HostListener } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { NavController, MenuController } from '@ionic/angular';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { User } from './registration.interface';
import { Utils } from '../../service/utils';
import { Constants } from '../../service/constants';
import { RegistrationProvider } from './registration.service';
import { Message } from '../../service/dialog/message.interface';
import _ from 'lodash';
import { ImageConstants } from '../../service/image-constants';

@Component({
  selector: 'page-registration',
  templateUrl: 'registration.html',
  providers: [RegistrationProvider],
  styleUrls: ['registration.scss']
})
export class RegistrationPage implements AfterViewInit {
  private registration: FormGroup;
  public tabs: Array<object>;
  private states: Array<State>;
  private countries: Array<Country>;
  public isApp: boolean;
  public isSubmitted: boolean;
  public isLoading: boolean;
  private selectedIndex: number;
  public agreement: boolean;
  private bannedUserNames: string[];
  private isBannedUsername: boolean = false;
  private disableNext: boolean = true;
  private domesticPhoneRegex: RegExp;
  private internationalPhoneRegex: RegExp;
  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    //debounce resize, wait for resize to finish before doing stuff
    this.onResize();
  }
  @ViewChild('tab1', { static: false }) tab1Content: TemplateRef<any>;
  @ViewChild('tab2', { static: false }) tab2Content: TemplateRef<any>;
  @ViewChild('tab3', { static: false }) tab3Content: TemplateRef<any>;
  constructor(
    public navCtrl: NavController,
    public menu: MenuController,
    private formBuilder: FormBuilder,
    public utils: Utils,
    public constants: Constants,
    public Registration: RegistrationProvider,
    public imageConstants: ImageConstants
  ) {
    this.registration = this.formBuilder.group(
      {
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        companyName: new FormControl('', [Validators.required]),
        jobTitle: new FormControl('', [Validators.required]),
        address: new FormControl('', [Validators.required]),
        city: new FormControl('', [Validators.required]),
        country: new FormControl('', [Validators.required]),
        state: new FormControl(''),
        postal: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.required, Validators.pattern(this.constants.EMAIL_REGEX)]),
        phone: new FormControl({ value: '', disabled: true }, [Validators.required]),
        password: new FormControl('', [
          Validators.required,
          Validators.pattern(this.constants.PASSWORD_REGEX),
          Validators.minLength(8)
        ]),
        confirmPassword: new FormControl('', [
          Validators.required,
          Validators.pattern(this.constants.PASSWORD_REGEX),
          Validators.minLength(8)
        ]),
        termAgreement: new FormControl('', [Validators.required])
      },
      { validator: Validators.compose([this.matchPasswordCheck, this.phoneNumberCheck]) }
    );

    this.Registration.getBannedUserNames().subscribe(response => {
      this.bannedUserNames = response;
    });

    this.Registration.getCountries().subscribe(response => {
      this.countries = [];
      _.forEach(response, country => {
        this.countries.push(new Country(country.name, country.code, country.states));
      });
    });

    this.domesticPhoneRegex = this.constants.DOMESTIC_PHONE_REGEX;
    this.internationalPhoneRegex = this.constants.INTERNATIONAL_PHONE_REGEX;

    this.menu.enable(false);
    this.isSubmitted = false;
    this.isLoading = false;
    this.states = [];
    this.selectedIndex = 0;
    this.onResize(); // determine if its the app using ionic
  }

  ngAfterViewInit() {
    this.tabs = [
      new tabsContent('Account Info', this.tab1Content),
      new tabsContent('Work Info', this.tab2Content),
      new tabsContent('Password', this.tab3Content)
    ];

    this.registration
      .get('email')
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => {
        if (_.trim(value).length > 0) {
          this.isBannedUsername = false;
          for (let i = 0; i < this.bannedUserNames.length; i++) {
            // need to extract the banned name before the @
            let emailUserName = value.indexOf('@') <= -1 ? value : this.utils.extractedUsername(value);

            if (this.bannedUserNames[i].toLowerCase() === emailUserName.toLowerCase()) {
              // yep match
              this.isBannedUsername = true;
              this.disableNext = true; // This doesn't seem to be used anywhere. Should this be disabling next() or is it no longer required?
              break;
            }
          }

          if (this.isBannedUsername) {
            let message: Message = {
              title: 'Email is not valid!',
              message: 'Please use a valid email address, group email addresses are not allowed.'
            };
            this.utils.openDialog(message, 'error', () => {
              this.disableNext = false; // the isBanned get reset after submit, but it should matter
            });
          }
        }
      });

    this.registration
      .get('country')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(() => {
        this.registration.get('phone').enable();
      });
  }

  mobileFormCheck = (fields: Array<string>) => {
    for (let i = 0; i < fields.length; i++) {
      if (_.trim(fields[i]) === '') {
        return false;
      }
    }
    return true;
  };

  next = () => {
    let fields = [];
    switch (this.selectedIndex) {
      case 0:
        fields = [
          this.registration.get('firstName').value,
          this.registration.get('lastName').value,
          this.registration.get('email').value
        ];
        break;
      case 1:
        fields = [
          this.registration.get('companyName').value,
          this.registration.get('jobTitle').value,
          this.registration.get('address').value,
          this.registration.get('city').value,
          this.registration.get('country').value,
          this.registration.get('jobTitle').value,
          this.registration.get('postal').value,
          this.registration.get('phone').value
        ];
        if (!!this.registration.controls['state'].validator) {
          fields.push(this.registration.get('state').value);
        }
        break;
      case 2:
        fields = [this.registration.get('password').value, this.registration.get('confirmPassword').value];
        break;
    }

    if (this.mobileFormCheck(fields)) {
      this.selectedIndex += 1;
    } else {
      switch (this.selectedIndex) {
        case 0:
          this.registration.get('firstName').markAsTouched();
          this.registration.get('firstName').updateValueAndValidity();

          this.registration.get('lastName').markAsTouched();
          this.registration.get('lastName').updateValueAndValidity();

          this.registration.get('email').markAsTouched();
          this.registration.get('email').updateValueAndValidity();
          break;
        case 1:
          this.registration.get('companyName').markAsTouched();
          this.registration.get('companyName').updateValueAndValidity();

          this.registration.get('jobTitle').markAsTouched();
          this.registration.get('jobTitle').updateValueAndValidity();

          this.registration.get('address').markAsTouched();
          this.registration.get('address').updateValueAndValidity();

          this.registration.get('city').markAsTouched();
          this.registration.get('city').updateValueAndValidity();

          this.registration.get('country').markAsTouched();
          this.registration.get('country').updateValueAndValidity();

          this.registration.get('state').markAsTouched();
          this.registration.get('state').updateValueAndValidity();

          this.registration.get('postal').markAsTouched();
          this.registration.get('postal').updateValueAndValidity();

          this.registration.get('phone').markAsTouched();
          this.registration.get('phone').updateValueAndValidity();
          break;
        case 2:
          this.registration.get('password').markAsTouched();
          this.registration.get('password').updateValueAndValidity();

          this.registration.get('confirmPassword').markAsTouched();
          this.registration.get('confirmPassword').updateValueAndValidity();
          break;
      }
    }
  };

  setStates = (selectedCountry: string) => {
    this.states = [];
    let countrySelected = _.find(this.countries, { name: selectedCountry });
    _.forEach(countrySelected.getStates(), states => {
      this.states.push(new State(states.name, states.code));
    });
    this.checkStates();
  };

  checkStates = () => {
    if (!!this.states && this.states.length > 0) {
      this.registration.controls['state'].setValidators([Validators.required]);
    } else {
      this.registration.controls['state'].clearValidators();
      this.registration.patchValue({ state: '' });
    }
  };

  onResize = () => {
    this.isApp = this.utils.isApp();
  };

  matchPasswordCheck = (control: AbstractControl): { [key: string]: boolean } => {
    const newPassword = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    // Don't kick in until user touches both fields
    if (newPassword.pristine || confirmPassword.pristine) {
      return null;
    }

    if (!newPassword || !confirmPassword) {
      return null;
    }

    return newPassword.value === confirmPassword.value ? null : { matchPasswordCheck: true };
  };

  phoneNumberCheck = (control: AbstractControl): { [key: string]: boolean } => {
    const country = control.get('country');
    const phoneNumber = control.get('phone');

    // Don't kick in until user touches both fields
    if (country.pristine || phoneNumber.pristine) {
      return null;
    }

    if (!country) {
      return null;
    }

    const domesticCountries: string[] = ['United States', 'Canada'];
    const phoneRegex: RegExp =
      domesticCountries.indexOf(country.value) > -1 ? this.domesticPhoneRegex : this.internationalPhoneRegex;

    const valid = phoneRegex.test(phoneNumber.value);
    let errors = phoneNumber.errors;

    if (!valid) {
      if (!errors) {
        errors = {};
      }
      errors['invalid'] = true;
      phoneNumber.setErrors(errors);
    } else {
      if (errors) {
        errors['invalid'] = null;
        if (Object.keys(errors).length === 0) {
          errors = null;
        }
        phoneNumber.setErrors(errors);
        phoneNumber.updateValueAndValidity();
      }
    }

    return valid ? null : { phoneNumberCheck: true };
  };

  onSubmit = ({ value, valid }: { value: User; valid: boolean }) => {
    this.isLoading = true;
    this.Registration.postRegistration(value).subscribe(
      response => {
        this.isSubmitted = true;
        this.isLoading = false;
        this.isBannedUsername = false;
        window.location.href = 'http://www.harveyperformance.com/map-thank-you/';
      },
      error => {
        this.isLoading = false;
        this.isBannedUsername = false;
        let message: Message = {
          title: 'Registration Error!',
          message: error
        };
        this.utils.openDialog(message, 'error');
      }
    );
  };

  cancel = () => {
    this.navCtrl.navigateForward('/login');
  };

  showDisclaimer = () => {
    let messageStr =
      'End User License Agreement: <a target="_blank" href="http://www.harveyperformance.com/machining-advisor-pro-software-end-user-license-agreement/">http://www.harveyperformance.com/machining-advisor-pro-software-end-user-license-agreement/</a><br />';
    messageStr +=
      'Privacy Policy: <a target="_blank" href="http://www.harveyperformance.com/machining-advisor-pro-privacy-policy/">http://www.harveyperformance.com/machining-advisor-pro-privacy-policy/</a>';
    let message: Message = {
      title: 'Disclaimer',
      message: messageStr
    };
    this.utils.openDialog(message, 'confirm');
  };
}

class tabsContent {
  constructor(public label: string, public content: TemplateRef<any>) {
    this.label = label;
    this.content = content;
  }
}

class BasicObject {
  private name: string;
  constructor(name: string) {
    this.name = name;
  }

  getName = (): string => {
    return this.name;
  };
}

class Country extends BasicObject {
  private countryCode: string;
  private states: object[];
  constructor(name: string, countryCode: string, states: object[]) {
    super(name);
    this.countryCode = countryCode;
    this.states = states;
  }

  getCountryCode = (): string => {
    return this.countryCode;
  };

  getStates = (): object[] => {
    return this.states;
  };
}

class State extends BasicObject {
  private abbreviation: string;
  constructor(name: string, abbreviation: string) {
    super(name);
    this.abbreviation = abbreviation;
  }
  getAbbeviation = (): string => {
    return this.abbreviation;
  };
}
